import { API_BASE } from "../config";
import {
  apiGet,
  apiPost,
  apiPut,
  authHeaders,
  authHeadersMultipart,
  buildApiError,
  handleAuthStatus,
  readJsonSafe,
} from "./client";
import {
  clearClientSession,
  setStoredUser,
  setToken,
  shouldRememberSession,
} from "./session";

async function finalizeLoginToken(json, remember) {
  if (!json?.token) return;
  setToken(json.token, remember);
}

export async function login(username, password, remember = true) {
  let res;
  try {
    res = await fetch(`${API_BASE}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password }),
    });
  } catch {
    throw new Error("تعذر الاتصال بالخادم. تأكد من تشغيل الخادم ثم حاول مرة أخرى.");
  }

  const json = await readJsonSafe(res);
  if (!res.ok) throw buildApiError(json, "فشل تسجيل الدخول", res.status);

  if (!json.requires_mfa && !json.requires_mfa_setup) {
    await finalizeLoginToken(json, remember);
  } else {
    clearClientSession();
  }

  const user = json.user || json;
  if (json.token && user?.username && user?.role) {
    setStoredUser(user, remember);
  }

  return json;
}

export async function verifyAdminMfaLogin({
  challengeToken,
  code = "",
  recoveryCode = "",
  remember = false,
}) {
  let res;
  try {
    res = await fetch(`${API_BASE}/auth/admin-mfa/verify-login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        challenge_token: challengeToken,
        code,
        recovery_code: recoveryCode,
      }),
    });
  } catch {
    throw new Error("تعذر الاتصال بالخادم. تأكد من تشغيل الخادم ثم حاول مرة أخرى.");
  }

  const json = await readJsonSafe(res);
  if (!res.ok) throw buildApiError(json, "فشل التحقق الثنائي", res.status);

  await finalizeLoginToken(json, remember);

  if (json.user?.username && json.user?.role) {
    setStoredUser(json.user, remember);
  }

  return json;
}

export async function beginAdminMfaBootstrapSetup({ challengeToken }) {
  return apiPost("/auth/admin-mfa/bootstrap-setup", {
    setup_challenge_token: challengeToken,
  });
}

export async function completeAdminMfaBootstrapSetup({
  setupToken,
  code,
  remember = false,
}) {
  const json = await apiPost("/auth/admin-mfa/bootstrap-enable", {
    setup_token: setupToken,
    code,
  });

  await finalizeLoginToken(json, remember);

  if (json.user?.username && json.user?.role) {
    setStoredUser(json.user, remember);
  }

  return json;
}

export async function register(data) {
  let res;
  try {
    res = await fetch(`${API_BASE}/auth/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
  } catch {
    throw new Error("تعذر الاتصال بالخادم. تأكد من تشغيل الخادم ثم حاول مرة أخرى.");
  }

  const json = await readJsonSafe(res);
  if (!res.ok) throw buildApiError(json, "فشل إنشاء الحساب", res.status);
  return json;
}

export async function fetchMyAccount() {
  const json = await apiGet("/me");
  setStoredUser(json, shouldRememberSession());
  return json;
}

async function putMyAccount(url, data) {
  const headers = await authHeaders();
  const res = await fetch(url, {
    method: "PUT",
    headers,
    body: JSON.stringify(data || {}),
  });

  const json = await readJsonSafe(res);
  handleAuthStatus(res, json);
  return { res, json };
}

export async function updateMyAccount(data) {
  const input = data && typeof data === "object" ? data : {};
  const payload = {};

  if (input.name !== undefined) payload.name = input.name;
  if (input.email !== undefined) payload.email = input.email;
  if (input.phone !== undefined) payload.phone = input.phone;
  if (input.gender !== undefined) payload.gender = input.gender;

  let out = await putMyAccount(`${API_BASE}/me`, payload);

  if (
    !out.res.ok &&
    (out.res.status === 404 || out.res.status === 405 || out.res.status === 501)
  ) {
    out = await putMyAccount(`${API_BASE}/auth/me`, payload);
  }

  if (!out.res.ok) {
    throw buildApiError(out.json, "فشل تحديث البيانات", out.res.status);
  }

  return fetchMyAccount();
}

export async function changeMyPassword({ oldPassword, newPassword }) {
  return apiPut("/auth/change-password", { oldPassword, newPassword });
}

export async function fetchAdminMfaStatus() {
  return apiGet("/auth/admin-mfa/status");
}

export async function beginAdminMfaSetup() {
  return apiPost("/auth/admin-mfa/setup");
}

export async function enableAdminMfa({ setupToken, code }) {
  return apiPost("/auth/admin-mfa/enable", {
    setup_token: setupToken,
    code,
  });
}

export async function disableAdminMfa({ code = "", recoveryCode = "" }) {
  return apiPost("/auth/admin-mfa/disable", {
    code,
    recovery_code: recoveryCode,
  });
}

export async function regenerateAdminMfaRecoveryCodes({ code }) {
  return apiPost("/auth/admin-mfa/recovery-codes/regenerate", { code });
}

export async function uploadLogo(file) {
  if (!file) throw new Error("اختر ملف الشعار أولًا");

  const fd = new FormData();
  fd.append("logo", file);

  const headers = await authHeadersMultipart();
  const res = await fetch(`${API_BASE}/upload/logo`, {
    method: "POST",
    headers,
    body: fd,
  });

  const json = await readJsonSafe(res);
  handleAuthStatus(res, json);
  if (!res.ok) throw buildApiError(json, "فشل رفع الشعار", res.status);
  return json;
}

export async function uploadBrandImage(file) {
  if (!file) throw new Error("اختر صورة الهوية أولًا");

  const fd = new FormData();
  fd.append("image", file);

  const headers = await authHeadersMultipart();
  const res = await fetch(`${API_BASE}/upload/brand-image`, {
    method: "POST",
    headers,
    body: fd,
  });

  const json = await readJsonSafe(res);
  handleAuthStatus(res, json);
  if (!res.ok) throw buildApiError(json, "فشل رفع صورة الهوية", res.status);
  return json;
}

export async function uploadTaskFile(file, fieldName = "file") {
  if (!file) throw new Error("اختر ملفًا أولًا");

  const fd = new FormData();
  fd.append(fieldName, file);

  const headers = await authHeadersMultipart();
  const res = await fetch(`${API_BASE}/tasks/upload`, {
    method: "POST",
    headers,
    body: fd,
  });

  const json = await readJsonSafe(res);
  handleAuthStatus(res, json);
  if (!res.ok) throw buildApiError(json, "تعذر رفع الملف", res.status);
  return json;
}
